import { AfterViewInit, Component, inject, ViewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatDialog } from '@angular/material/dialog';
import { CommonModule, CurrencyPipe, DatePipe } from "@angular/common";
import { ViewReceiptDialogComponent } from "@app/components/view-receipt-dialog/view-receipt-dialog.component";

@Component({
  selector: 'app-receipt',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    CommonModule,
    CurrencyPipe,
    DatePipe,
  ],
  templateUrl: './receipt.component.html',
  styleUrl: './receipt.component.scss'
})
export class ReceiptComponent implements AfterViewInit {
  private _dialog = inject(MatDialog);

  displayedColumns: string[] = ['network', 'location', 'energy', 'duration', 'total', 'date', 'status'];
  dataSource = new MatTableDataSource<ReceiptRow>(RECEIPT_DATA);

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onRowClick(receipt: ReceiptRow): void {
    this._dialog.open(ViewReceiptDialogComponent, {
      maxWidth: '600px',
      width: '600px',
      data: receipt
    });
  }

  getStatusClass(status: string): string {
    const statusMap: Record<string, string> = {
      'Completed': 'status-completed',
      'Pending': 'status-pending',
      'Failed': 'status-failed',
      'Refunded': 'status-refunded'
    };
    return statusMap[status] || 'status-default';
  }

  formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  }
}

export interface ReceiptRow {
  id: string;
  networkName: string;
  networkLogo: string;
  locationName: string;
  address: string;
  energyKwh: number;
  durationMinutes: number;
  totalAmount: number;
  date: Date;
  status: 'Completed' | 'Pending' | 'Failed' | 'Refunded';
  sessionId: string;
  stationId: string;
  connectorNumber: number;
  powerType: string;
  maxPower: number;
  chargingFee: number;
  tax: number;
}

const RECEIPT_DATA: ReceiptRow[] = [
  {
    id: '1',
    networkName: 'Shell Recharge',
    networkLogo: 'assets/images/shell-logo.png',
    locationName: 'Tesla Destination Charger',
    address: '2675 Geary Blvd, San Francisco, CA 94118',
    energyKwh: 45.2,
    durationMinutes: 62,
    totalAmount: 18.50,
    date: new Date('2025-07-16'),
    status: 'Completed',
    sessionId: 'SESS-001234',
    stationId: 'J3fd29FS',
    connectorNumber: 4,
    powerType: 'DC Fast',
    maxPower: 150,
    chargingFee: 17.00,
    tax: 1.50
  },
  {
    id: '2',
    networkName: 'ChargePoint',
    networkLogo: 'assets/images/chargepoint-logo.png',
    locationName: 'Whole Foods Market',
    address: '399 4th St, San Francisco, CA 94107',
    energyKwh: 22.8,
    durationMinutes: 45,
    totalAmount: 9.75,
    date: new Date('2025-07-14'),
    status: 'Completed',
    sessionId: 'SESS-001233',
    stationId: 'CP-SF-042',
    connectorNumber: 2,
    powerType: 'AC Level 2',
    maxPower: 11,
    chargingFee: 9.00,
    tax: 0.75
  },
  {
    id: '3',
    networkName: 'EVgo',
    networkLogo: 'assets/images/evgo-logo.png',
    locationName: 'Safeway Parking',
    address: '730 Taraval St, San Francisco, CA 94116',
    energyKwh: 38.5,
    durationMinutes: 35,
    totalAmount: 15.40,
    date: new Date('2025-07-12'),
    status: 'Completed',
    sessionId: 'SESS-001232',
    stationId: 'EVG-78421',
    connectorNumber: 1,
    powerType: 'DC Fast',
    maxPower: 100,
    chargingFee: 14.20,
    tax: 1.20
  },
  {
    id: '4',
    networkName: 'Electrify America',
    networkLogo: 'assets/images/ea-logo.png',
    locationName: 'Target Parking Garage',
    address: '789 Mission St, San Francisco, CA 94103',
    energyKwh: 0,
    durationMinutes: 0,
    totalAmount: 0,
    date: new Date('2025-07-10'),
    status: 'Failed',
    sessionId: 'SESS-001231',
    stationId: 'EA-CAL-915',
    connectorNumber: 3,
    powerType: 'DC Fast',
    maxPower: 350,
    chargingFee: 0,
    tax: 0
  },
  {
    id: '5',
    networkName: 'Tesla Supercharger',
    networkLogo: 'assets/images/tesla-logo.png',
    locationName: 'Tesla Service Center',
    address: '901 Van Ness Ave, San Francisco, CA 94109',
    energyKwh: 52.1,
    durationMinutes: 28,
    totalAmount: 22.35,
    date: new Date('2025-07-08'),
    status: 'Completed',
    sessionId: 'SESS-001230',
    stationId: 'TSC-SF-003',
    connectorNumber: 8,
    powerType: 'DC Fast',
    maxPower: 250,
    chargingFee: 20.50,
    tax: 1.85
  }
];
